import { IPathfindingGoalCondition } from "./IPathfindingGoalCondition";
import { IntRectangle } from "./IntRectangle";
import { IntPoint } from "./IntPoint";
import { PathfindingGraph } from "./PathfindingGraph";

/**
 * A goal condition for the Pathfinder that is satisfied on any node within a given rectangle.
 * @author npuschke
 *
 */
export class PathfindingGoalConditionTargetRect implements IPathfindingGoalCondition {
    private _target: IntRectangle;

    /**
     * Creates a GoalCondition that is satisfied within the given rectangle.
     * @param target The rectangle that defines the target area for the search.
     *
     */
    constructor(target: IntRectangle) {
        this._target = target;
    }

    /**
     * Determines whether this goal condition is satisfied at the given coordinates.
     * @param x The horizontal coordinate of the potential target node.
     * @param y The vertical coordinate of the potential target node.
     * @return <code>true</code> if the node is the target; <code>false</code> otherwise.
     *
     */
    public isSatisfied(x: number, y: number): boolean {
        return this._target.containsPoint(new IntPoint(x, y));
    }

    /**
     * Roughly checks if the goal condition is satisfied for any node in the graph at all. If not, the path search can be aborted early.
     * @param graph The graph that will be searched.
     * @return <code>true</code> if no node satisfies the goal condition; <code>false</code> otherwise.
     *
     */
    public isUnreachable(graph: PathfindingGraph): boolean {
        for (let x: number = this._target.x; x < this._target.right; x++) {
            for (let y: number = this._target.y; y < this._target.bottom; y++) {
                if (graph.hasNodeAtPosition(x, y)) {
                    return false;
                }
            }
        }
        return true;
    }
}

